import { takeEvery } from 'redux-saga';
import { hashHistory } from 'react-router';
import envDetect from '@tcc/env-detect';
import { put, call, fork, select } from 'redux-saga/effects';
import {
  loginSuccess,
  loginFailure,
  loggedInCheckSuccess,
  loggedInCheckFailure,
  logoutSuccess,
  logoutFailure
} from 'actions/loginActions';
import {
  loginRequest,
  loggedInCheckRequest,
  clearCookiesRequest
} from 'requesters/loginRequester';
import { LOGIN_REQUEST, LOGOUT_REQUEST, LOGGED_IN_CHECK_REQUEST } from 'constants/actionTypes';
import { logoutUrls } from 'constants/logoutUrls';
import {
  trackLogin,
  trackLogout,
  trackGetAuthToken,
  trackAutoLoginSuccess,
  trackAutoLoginFailure,
  trackIncorrectEmailOrPassword,
  trackStayLoggedInSelected,
  trackLoginSuccess
} from 'utils/analytics/loginAnalytics';
import { clearSessionStorage } from 'utils/clearSession';
import { redirect } from 'utils/url';

import { redirectUser } from './redirectionSaga';

/**
 * Login API call.
 * On success, dispatch loginSuccess with the response containing the auth token
 * On failure, dispatch loginFailure with the response error code
 * @param {String} username
 * @param {String} password
 * @param {Boolean} hasSelectedStayLoggedIn
 * @param {Number} startTime
 * @return {void}
 */
export function *login(username, password, hasSelectedStayLoggedIn, startTime) {

  if (!username && !password) {
    return yield put(loginFailure('missingUsernamePassword'));
  }
  if (!username) {
    return yield put(loginFailure('missingUsername'));
  }
  if (!password) {
    return yield put(loginFailure('missingPassword'));
  }

  try {
    const response = yield call(loginRequest, username, password);
    if (hasSelectedStayLoggedIn) {
      trackStayLoggedInSelected();
    }
    trackLogin();
    trackGetAuthToken();
    trackLoginSuccess(Date.now() - startTime);
    yield put(loginSuccess(response.user));
    return yield call(redirectUser);

  } catch (error) {
    const errorNumber = parseInt(error.status, 10);
    if (errorNumber >= 400 && errorNumber < 500) {
      trackIncorrectEmailOrPassword();
      return yield put(loginFailure('failedLogin'));
    }
    return yield put(loginFailure('serverError'));
  }
}

/**
 * Logout API call
 * @param {Object} action
 * @return {void}
 */
export function *logout(action) {
  try {
    const { countryCode } = action;
    const upperCaseCountryCode = countryCode && countryCode.toUpperCase();

    if (upperCaseCountryCode) {
      localStorage.setItem('referrerCountryCode', upperCaseCountryCode);
    }

    yield call(hashHistory.push, '/logout');
    yield call(clearCookiesRequest);
    clearSessionStorage();
    yield put(logoutSuccess());
    trackLogout();

    const { LOCAL, PRODUCTION } = envDetect.environments;

    if (!envDetect.isEnv(PRODUCTION)) {
      redirect(envDetect.isEnv(LOCAL) ? logoutUrls.local : logoutUrls.base);
    } else {
      const state = yield select();
      const logoutUrl = state.supportInfo.landingPageUrls[upperCaseCountryCode] ||
        state.supportInfo.landingPageUrls.US || logoutUrls.prodDefault;
      redirect(logoutUrl);
    }

  } catch (error) {
    yield put(logoutFailure());
  }
}

/**
* LoggedInCheck API call - Check if the user is already logged in
* @return {Object}
*/
export function *loggedInCheck() {
  try {
    const response = yield call(loggedInCheckRequest);
    trackAutoLoginSuccess();
    trackLogin();
    yield put(loggedInCheckSuccess(response.user));
    yield call(redirectUser);

    return response;
  } catch (error) {
    trackAutoLoginFailure();
    yield put(loggedInCheckFailure());

    return null;
  }
}

export function *processLogin() {
  const state = yield select();
  const {
    username,
    password,
    hasSelectedStayLoggedIn,
    startTime
  } = state.login;
  yield call(login, username, password, hasSelectedStayLoggedIn, startTime);
}

/**
 * Waits for the return of the LOGIN_REQUEST action event, pulls username and password
 * from appState, and calls the login saga
 * @return {void}
 */
export function *loginWatcher() {
  yield* takeEvery(LOGIN_REQUEST, processLogin);
}

/**
 * Waits for the return of the LOGIN_REQUEST action event, pulls username and password
 * from appState, and calls the login saga
 * @return {void}
 */
export function *logoutWatcher() {
  yield *takeEvery(LOGOUT_REQUEST, logout);
}

/**
 * Waits for the return of the LOGGED_IN_CHECK_REQUEST action event and calls the loggedInCheck saga
 * @return {void}
 */
function *loggedInCheckWatcher() {
  yield *takeEvery(LOGGED_IN_CHECK_REQUEST, loggedInCheck);
}

/**
 * Uses fork to start multiple watchers in parallel
 * @return {void}
 */
export default function *rootSaga() {
  yield fork(loginWatcher);
  yield fork(logoutWatcher);
  yield fork(loggedInCheckWatcher);
}
